import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, ResultTable } from '../_fitnessHelpers';
import { FullPregnancyTable, CycleCalendar } from './_pregnancyHelpers';

const fmt = d => d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const fmtShort = d => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function ConceptionCalc() {
  const [method, setMethod] = useState('lmp');
  const [lmp, setLmp] = useState(new Date().toISOString().slice(0, 10));
  const [cycle, setCycle] = useState(28);
  const [due, setDue] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 280); return d.toISOString().slice(0, 10); });
  const clear = () => { setMethod('lmp'); setLmp(new Date().toISOString().slice(0, 10)); setCycle(28); };

  const c = useMemo(() => {
    const lmpDate = new Date(lmp);
    const luteal = 14; // luteal phase is fixed ~14 days
    const ovulationDay = (+cycle || 28) - luteal;
    let ovulation;
    if (method === 'lmp') ovulation = addDays(lmpDate, ovulationDay);
    else ovulation = addDays(new Date(due), -266);
    const fertileStart = addDays(ovulation, -5);
    const fertileEnd = addDays(ovulation, 1);
    const nextPeriod = addDays(lmpDate, +cycle || 28);
    const dueDate = addDays(ovulation, 266);
    return { ovulation, fertileStart, fertileEnd, nextPeriod, dueDate, ovulationDay, lmpDate };
  }, [method, lmp, cycle, due]);

  // Build cycle calendar (one row = cycle length days)
  const days = useMemo(() => {
    const n = +cycle || 28;
    const arr = [];
    for (let i = 0; i < n; i++) {
      const d = addDays(c.lmpDate, i);
      const isPeriod = i < 5;
      const isFertile = d >= c.fertileStart && d <= c.fertileEnd;
      const isOvulation = sameDay(d, c.ovulation);
      arr.push({ d, i, isPeriod, isFertile, isOvulation });
    }
    return arr;
  }, [c, cycle]);

  return (
    <CalcShell look="cnet" title="Pregnancy Conception Calculator" subtitle="Estimate the likely conception date, fertile window, ovulation day, next period and due date — plus a full cycle calendar.">
      <CalcNetPanel slug="conc" tabs={['Conception']} activeTab={0} onTabChange={() => {}} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Likely conception date</div>
          <div className="text-xl font-bold text-[#2f9c3f]" data-testid="conc-date">{fmt(c.ovulation)}</div>
          <ResultTable testid="conc-window-table" rows={[
            ['Ovulation day (day ' + c.ovulationDay + ' of cycle)', <span className="font-bold text-emerald-700">{fmt(c.ovulation)}</span>],
            ['Fertile window start', fmt(c.fertileStart)],
            ['Fertile window end', fmt(c.fertileEnd)],
            ['Next expected period', fmt(c.nextPeriod)],
            ['Estimated due date (if conception occurs)', <span className="font-bold text-blue-700">{fmt(c.dueDate)}</span>],
          ]} />
          <p className="text-xs text-slate-600 mt-2">Note: fertile window = 5 days before ovulation + ovulation day + 1 day after (sperm can survive up to 5 days).</p>
        </div>
      }>
        <Row label="Calculate By">
          <select value={method} onChange={e => setMethod(e.target.value)} data-testid="conc-method" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
            <option value="lmp">First Day of Last Period (LMP)</option>
            <option value="due">Estimated Due Date</option>
          </select>
        </Row>
        {method === 'lmp' ? <>
          <Row label="LMP Date">
            <input type="date" value={lmp} onChange={e => setLmp(e.target.value)} data-testid="conc-lmp" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
          </Row>
          <Row label="Average Cycle Length"><UnitInput value={cycle} onChange={setCycle} unit="days" testid="conc-cycle" /></Row>
          <div className="text-xs text-slate-500 pl-40">Most women have a 21-35 day cycle. Default is 28.</div>
        </> : (
          <Row label="Due Date">
            <input type="date" value={due} onChange={e => setDue(e.target.value)} data-testid="conc-due" className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" />
          </Row>
        )}
      </CalcNetPanel>

      {/* Cycle calendar heatmap */}
      <CycleCalendar lmpDate={c.lmpDate} cycleLen={cycle} ovulationDay={c.ovulationDay} testId="conc-calendar" />

      <FullPregnancyTable dueDate={c.dueDate} currentWeek={0} testId="conc-full-table" />

      {/* Ovulation-day prediction table for next 6 cycles */}
      <div className="mt-6">
        <h3 className="font-bold text-slate-900 mb-2">Ovulation Prediction — Next 6 Cycles</h3>
        <table className="w-full text-sm border border-slate-300" data-testid="conc-prediction-table">
          <thead className="bg-[#1e40af] text-white">
            <tr><th className="px-3 py-2 text-left">Cycle</th><th className="px-3 py-2 text-left">Ovulation Date</th><th className="px-3 py-2 text-left">Fertile Window</th><th className="px-3 py-2 text-left">Next Period</th><th className="px-3 py-2 text-left">Est. Due Date</th></tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }, (_, k) => {
              const offset = k * (+cycle || 28);
              const ov = addDays(c.ovulation, offset);
              const fs = addDays(c.fertileStart, offset);
              const fe = addDays(c.fertileEnd, offset);
              const np = addDays(c.nextPeriod, offset);
              const dd = addDays(ov, 266);
              return (
                <tr key={k} className={k % 2 ? 'bg-slate-50' : ''}>
                  <td className="px-3 py-1.5">#{k + 1} {k === 0 ? '(current)' : ''}</td>
                  <td className="px-3 py-1.5 font-mono">{fmtShort(ov)}</td>
                  <td className="px-3 py-1.5 font-mono">{fmtShort(fs)} – {fmtShort(fe)}</td>
                  <td className="px-3 py-1.5 font-mono">{fmtShort(np)}</td>
                  <td className="px-3 py-1.5 font-mono">{fmtShort(dd)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CalcShell>
  );
}
